import React, { useState, ReactNode, useEffect } from "react";

import { Flex, Text } from "flicket-ui";
import { useIsMobile } from "~hooks";
import { Icon, IconProps } from "./Icon";
import { useRouter } from "next/router";
import omit from "lodash/omit";
import { ParsedUrlQuery } from "querystring";

type TabItemProps = {
  id: string;
  title: string;
  icon?: IconProps["icon"];
  content: ReactNode;
};

type TabProps = {
  initialTab?: TabItemProps | string;
  items: TabItemProps[];
  tabProps?: any;
};

export const Tabs = ({ initialTab, items, tabProps = {} }: TabProps) => {
  const isMobile = useIsMobile();
  const router = useRouter();
  const { tab } = router.query;
  const [activeTab, setActiveTab] = useState<TabItemProps>(
    typeof initialTab === "string"
      ? items.find(({ id }) => id === initialTab)
      : tab
      ? items.find(({ id }) => id === (tab as string))
      : initialTab ?? items[0]
  );

  useEffect(() => {
    if (typeof tab !== "string") return;
    if (activeTab.id === tab) return;

    const newTab = items.find(({ id }) => id === tab);
    if (!newTab) return;

    setActiveTab(newTab);
  }, [tab]);

  const handleTabChange = (newTab: string) => {
    const params: ParsedUrlQuery = omit(router.query, ["tab"]);
    void router.push(
      {
        pathname: router.pathname,
        query: { ...params, tab: newTab },
      },
      undefined,
      {
        shallow: true,
      }
    );
  };

  useEffect(() => {
    if (!tab && !activeTab) {
      setActiveTab(items[0]);
    }
  }, [tab]);

  return (
    <>
      <Flex borderBottom="1px solid" borderColor="N200" w="100%">
        {items.map(({ id, title, content, ...props }) => (
          <Flex
            key={id}
            onClick={() => handleTabChange(id)}
            cursor="pointer"
            alignItems="center"
            pb={1}
            borderBottom="4px solid"
            borderColor={activeTab.id === id ? "P300" : "transparent"}
            mr={{
              _: items[items.length - 1].id === id ? 0 : 3,
              md: items[items.length - 1].id === id ? 0 : 7,
            }}
            ml={{
              _: 0,
              md: items[0].id === id ? 4 : 0,
            }}
          >
            {/* eslint-disable-next-line react/prop-types */}
            {props?.icon && !isMobile && (
              //eslint-disable-next-line react/prop-types
              <Icon fontSize={4} icon={props.icon} mr={1} />
            )}
            <Text
              variant="header.XS"
              color={activeTab.id === id ? "N800" : "N500"}
            >
              {title}
            </Text>
          </Flex>
        ))}
      </Flex>
      <Flex px={{ _: 0, md: 4 }} pt={{ _: 3, md: 4 }} pb={4} flexWrap="wrap">
        {typeof activeTab.content === "function" ? (
          <activeTab.content {...tabProps} />
        ) : (
          activeTab.content
        )}
      </Flex>
    </>
  );
};
